import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import DynamicIcon from "./DynamicIcon";

interface MaterialIcon {
  name: string;
  categories: string[];
  tags: string[];
  popularity: number;
}

interface IconOption {
  icon: MaterialIcon;
  matchType: "name-prefix" | "name-contains" | "tag";
  matchedTag?: string;
}

interface IconPickerProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  label?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

let cachedIcons: MaterialIcon[] | null = null;

async function loadIcons(): Promise<MaterialIcon[]> {
  if (cachedIcons) return cachedIcons;
  const res = await fetch("/icons/material-icons.json");
  const data = await res.json();
  cachedIcons = data.icons as MaterialIcon[];
  return cachedIcons;
}

function searchIcons(icons: MaterialIcon[], query: string): IconOption[] {
  if (!query) return [];

  const q = query.toLowerCase();
  const prefixMatches: IconOption[] = [];
  const containsMatches: IconOption[] = [];
  const tagMatches: IconOption[] = [];

  for (const icon of icons) {
    const name = icon.name.toLowerCase();
    if (name.startsWith(q)) {
      prefixMatches.push({ icon, matchType: "name-prefix" });
    } else if (name.includes(q)) {
      containsMatches.push({ icon, matchType: "name-contains" });
    } else {
      const matchedTag = icon.tags.find((t) => t.toLowerCase().includes(q));
      if (matchedTag) {
        tagMatches.push({ icon, matchType: "tag", matchedTag });
      }
    }
  }

  const byPopularity = (a: IconOption, b: IconOption) =>
    b.icon.popularity - a.icon.popularity;

  prefixMatches.sort(byPopularity);
  containsMatches.sort(byPopularity);
  tagMatches.sort(byPopularity);

  return [...prefixMatches, ...containsMatches, ...tagMatches].slice(0, 20);
}

const IconPicker = (props: IconPickerProps) => {
  const { value, onChange, label = "Icon", disabled, sx } = props;
  const [icons, setIcons] = useState<MaterialIcon[]>([]);
  const [options, setOptions] = useState<IconOption[]>([]);
  const [inputValue, setInputValue] = useState("");
  const lastQuery = useRef("");

  useEffect(() => {
    loadIcons().then(setIcons);
  }, []);

  useEffect(() => {
    if (value && !inputValue) {
      setInputValue(value);
    }
  }, [value]);

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
    reason: string,
  ) => {
    setInputValue(newValue);
    if (reason === "input") {
      lastQuery.current = newValue;
      setOptions(searchIcons(icons, newValue));
    }
    if (reason === "clear") {
      setOptions([]);
      onChange?.(null);
    }
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: IconOption | string | null,
  ) => {
    if (typeof newValue === "string") {
      onChange?.(newValue || null);
      setInputValue(newValue);
    } else {
      onChange?.(newValue?.icon.name ?? null);
      if (newValue) {
        setInputValue(newValue.icon.name);
      }
    }
  };

  const handleBlur = () => {
    if (inputValue !== (value ?? "")) {
      onChange?.(inputValue || null);
    }
  };

  return (
    <Autocomplete<IconOption, false, false, true>
      freeSolo
      clearOnBlur={false}
      value={options.find((o) => o.icon.name === value) ?? null}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.icon.name)}
      isOptionEqualToValue={(option, val) => option.icon.name === val.icon.name}
      disabled={disabled}
      noOptionsText={inputValue ? "No icons found" : "Type to search icons"}
      sx={sx}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={option.icon.name} {...rest}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}
            >
              <DynamicIcon icon={option.icon.name} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {option.icon.name.replace(/_/g, " ")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {option.icon.categories.join(", ")}
                  </Typography>
                  {option.matchedTag && (
                    <Typography variant="caption" color="primary" noWrap>
                      matched: {option.matchedTag}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="filled"
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: value ? <DynamicIcon icon={value} /> : undefined,
            },
          }}
        />
      )}
    />
  );
};

export default IconPicker;
